/* global __webpack_public_path__ */
import type { Data, User } from "@eai-toolkit/client";
import { DataApi, MeApi } from "@eai-toolkit/client";
import { constructOrderParameter } from "@store/utils/sortUtils";
import axios from "axios";
import type { Dispatch, SetStateAction } from "react";
import UTIF from "utif";

import { DEFAULT_PAGE_LIMIT } from "@helpers/constants";

import { type FieldName, fields } from "../data/fields";
import type { ApiFile, ClientFile } from "./utils";
import { getExtension, getFileType, multiPartParseStream } from "./utils";

let basePath = __webpack_public_path__.includes("://")
  ? __webpack_public_path__
  : `${window.location.origin}${__webpack_public_path__}`;
if (basePath.slice(-1) === "/") {
  basePath = basePath.slice(0, -1);
}
basePath = basePath + "/webapp";

const axiosInstance = axios.create({
  baseURL: basePath,
  withCredentials: process.env.NODE_ENV !== "development",
});

const dataApi = new DataApi(undefined, undefined, axiosInstance);
const meApi = new MeApi(undefined, undefined, axiosInstance);

export const ITEMS_PER_PAGE = DEFAULT_PAGE_LIMIT;

export async function listData(
  _key: string,
  page: number,
  searchFields: { [key in FieldName | "endDate"]?: string },
  fieldSortedBy: FieldName | null,
  fieldSortDirection: "asc" | "desc",
  permission: "data:get" | "data:read" | "data:write",
) {
  const id = searchFields[fields.id.fieldName];

  if (id) {
    try {
      const result = await getDataDetailsById(_key, id);
      return {
        items: [result],
        metadata: {
          total: 1,
          offset: ITEMS_PER_PAGE,
          limit: ITEMS_PER_PAGE,
          q: "query",
        },
      };
    } catch (e) {
      console.error(e);
      return {
        items: [],
        metadata: {
          total: 0,
          offset: ITEMS_PER_PAGE,
          limit: ITEMS_PER_PAGE,
          q: "query",
        },
      };
    }
  } else {
    const query = createSearchFieldsString(searchFields);

    const order = constructOrderParameter(fieldSortedBy, fieldSortDirection);

    const params = {
      human: true,
      limit: ITEMS_PER_PAGE,
      offset: ITEMS_PER_PAGE * page,
      q: query,
      order,
      permission: [permission],
    };

    try {
      const { data } = await dataApi.v1ClusterDataGet(params);

      return {
        // sometimes items can come back [] but there is an offset or limit - what to do
        items: data.items || [],
        metadata: {
          total: data.metadata?.total || data.items.length,
          offset: ITEMS_PER_PAGE * page,
          limit: ITEMS_PER_PAGE,
          q: query,
        },
      };
    } catch (e) {
      console.log("ERROR", e);
      // important to return this otherwise there is an infinite fetching loop
      return {
        items: [],
        metadata: {
          total: 0,
          offset: ITEMS_PER_PAGE * page,
          limit: ITEMS_PER_PAGE,
          q: query,
        },
      };
    }
  }
}

// ITEMS_PER_PREFETCH needs to be a multiple of ITEMS_PER_PAGE
export const ITEMS_PER_PREFETCH = ITEMS_PER_PAGE * 20;

export async function prefetchData(
  setReady: Dispatch<SetStateAction<boolean | number>>,
  count: number,
  fieldSortedBy: FieldName | null,
  fieldSortDirection: "asc" | "desc",
) {
  const order = constructOrderParameter(fieldSortedBy, fieldSortDirection);
  const url = `${basePath}/v1/cluster/data?limit=${ITEMS_PER_PREFETCH}&offset=${ITEMS_PER_PREFETCH * count}&human=1${order}`;
  // TODO: replace with dataApi call
  const { data } = await axiosInstance.get(url);
  // const { data } = await dataApi.v1ClusterDataGet({
  //   offset: ITEMS_PER_PREFETCH * count,
  //   limit: ITEMS_PER_PREFETCH,
  //   human: true,
  //   order,
  // });
  setReady(true);
  return data;
}

export const getData = async (dataId: string) => {
  const { data } = await dataApi.v1DataGetById({ dataId, human: true });
  return data;
};

const createSearchFieldsString = (searchFields: {
  [key in FieldName | "endDate"]?: string;
}) => {
  let queryFields: string[] = [];
  for (const [field, value] of Object.entries(searchFields)) {
    if (!value) {
      continue;
    }
    if (field === "fullName") {
      queryFields.push(`fullName__ilike=%25${value}%25`);
    }
    if (field === "creator") {
      queryFields.push(`${field}__eq=${value}`);
    }
    if (field === "endDate" && searchFields.created) {
      const endDate = new Date(value);
      endDate.setHours(endDate.getHours() + 24);
      const adjustedEndDate = endDate.toISOString();
      queryFields.push(
        `created__between=${searchFields.created},${adjustedEndDate}`,
      );
    }
    if (field === "created" && !searchFields.endDate) {
      const endDate = new Date(value);
      endDate.setHours(endDate.getHours() + 24);
      const adjustedEndDate = endDate.toISOString();
      queryFields.push(
        `created__between=${searchFields.created},${adjustedEndDate}`,
      );
    }
  }
  queryFields.push("newparser=1");
  return queryFields.join("&");
};

type ReturnDetailsById = Data;

export async function getDataDetailsById(
  _key: string,
  dataId: string,
): Promise<ReturnDetailsById> {
  return getData(dataId);
}

function createClientFile(file: ApiFile): ClientFile {
  const extension = getExtension(file.name);
  return {
    ...file,
    extension,
    type: getFileType(extension, file.kind),
  };
}

function parseListingLines(lines: string[]): ClientFile[] {
  return lines
    .filter((line) => line.trim())
    .map((line) => createClientFile(JSON.parse(line) as ApiFile));
}

export function getDataFolderStream(dataId: string, path: string) {
  const params = new URLSearchParams();
  params.append("kind", "ls");
  params.append("all", "true");
  params.append("depth", "1");
  params.append("lsversion", "v2");
  // The API matches root as a raw prefix, so "a/b" would also list "a/bc".
  const root = path.replace(/^\//, "");
  params.append("root", root && !root.endsWith("/") ? `${root}/` : root);

  const abortController = new AbortController();
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  const decoder = new TextDecoder("utf-8");
  let leftoverString: string | undefined;
  let cancelled = false;
  let lastPullPromise:
    | Promise<ReadableStreamReadResult<Uint8Array>>
    | undefined;

  return new ReadableStream<ClientFile[]>({
    async start(controller) {
      const response = await fetch(
        `${basePath}/v1/data/${dataId}/tree?${params.toString()}`,
        {
          credentials:
            process.env.NODE_ENV === "development" ? undefined : "include",
          signal: abortController.signal,
        },
      );
      if (cancelled) {
        return;
      }
      // Error responses carry JSON, not a listing; parsing them would leave the
      // column loading forever, so treat them as an empty folder.
      if (!response.ok) {
        controller.enqueue([]);
        controller.close();
        cancelled = true;
        abortController.abort();
        return;
      }

      // Clone response to avoid ReadableStream conflict with OpenReplay tracker
      const clonedResponse = response.clone();

      if (!clonedResponse.body) {
        return Promise.reject(new Error("Expected body stream"));
      }
      reader = clonedResponse.body.getReader();
    },
    async pull(controller) {
      if (cancelled || !reader) {
        return;
      }
      try {
        // A chunk can end before the first complete listing entry.
        while (!cancelled) {
          lastPullPromise = reader.read();
          const { done, value } = await lastPullPromise;
          if (cancelled) {
            return;
          }
          if (done) {
            const last = (leftoverString || "") + decoder.decode();
            controller.enqueue(parseListingLines([last]));
            controller.close();
            return;
          }
          // `stream` keeps a character split across chunks for the next one.
          const decodedString =
            (leftoverString || "") + decoder.decode(value, { stream: true });
          const lines = decodedString.split("\n");
          // The leftoverString is whatever follows the last newline character.
          // We save it since it may be part of a JSON object that hasn't yet
          // completed streaming. If the last character is a newline, then
          // leftoverString will be empty.
          leftoverString = lines.pop();
          const files = parseListingLines(lines);
          // An empty batch means the end to readers, so keep reading.
          if (files.length) {
            controller.enqueue(files);
            return;
          }
        }
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          // Readers would otherwise wait forever for the next batch.
          controller.error(e);
        }
      }
    },
    async cancel() {
      cancelled = true;
      // Cancelling only the clone leaves the original fetch response alive.
      // Abort first so a pending read cannot hold up cancellation.
      abortController.abort();
      await lastPullPromise?.catch(() => {});
      reader?.releaseLock();
    },
  });
}

/**
 * Whether a folder (the data root when `path` is empty) lists nothing. Stops
 * after the first entries, so a large folder costs no more than a small one.
 * A listing that fails also counts as empty.
 */
export async function isFolderEmpty(dataId: string, path: string) {
  const reader = getDataFolderStream(dataId, path).getReader();
  try {
    const { value } = await reader.read();
    return !value?.length;
  } finally {
    reader.cancel().catch(() => {});
  }
}

/**
 * Server route that streams a folder (all data when `path` is empty) as a
 * .tar. Used as a plain link so the browser streams it to disk with progress.
 */
export function getFolderDownloadUrl(dataId: string, path: string) {
  const params = new URLSearchParams({ path });
  return `${__webpack_public_path__}api/data/${encodeURIComponent(dataId)}/download?${params}`;
}

/**
 * Server route that streams one file as an attachment. Used as a plain link:
 * a fetched file is held in memory, which fails for large files.
 */
export function getFileDownloadUrl(dataId: string, path: string) {
  const params = new URLSearchParams({ path });
  return `${__webpack_public_path__}api/data/${encodeURIComponent(dataId)}/file?${params}`;
}

/**
 * Starts a download from code (e.g. a table row action). The response is an
 * attachment, so the browser saves it and the page stays where it is.
 */
export function startDownload(url: string) {
  window.location.assign(url);
}

// TODO - add ability to cancel request
export async function fetchFileSourceUrl(
  _key: string,
  dataId: string,
  mapping: string,
) {
  const response = await fetch(
    `${basePath}/v1/data/${dataId}/pull?mapping=${mapping}&version=latest`,
    {
      credentials:
        process.env.NODE_ENV === "development" ? undefined : "include",
    },
  );
  const result = await response.formData();
  const file = result.get("files") as File;
  return URL.createObjectURL(file);
}

export async function fetchText(
  _key: string,
  dataId: string,
  filepath: string,
): Promise<{ content: string; isComplete: boolean }> {
  const controller = new AbortController(); // Create an AbortController
  const response = await fetch(
    `${basePath}/v1/data/${dataId}/pull?mapping=${filepath}&version=latest`,
    {
      credentials:
        process.env.NODE_ENV === "development" ? undefined : "include",
      signal: controller.signal, // Pass the signal to the fetch request
    },
  );

  const stream = multiPartParseStream(response); // Pass the controller to the stream
  const reader = stream.getReader();
  let content = "";
  let isComplete = false;
  const charLimit = 100000;

  while (!isComplete && content.length < charLimit) {
    const { done, value } = await reader.read();
    if (done) {
      isComplete = true;
    } else if (value) {
      content += value.fileChunk;
    }

    // Check if we have enough content and abort the stream
    if (content.length >= charLimit) {
      controller.abort(); // Abort the fetch request
      reader.releaseLock(); // Release the reader lock
      break; // Exit the loop
    }
  }
  if (!isComplete) {
    content = content.slice(0, charLimit);
  }
  return {
    content,
    isComplete,
  };
}

export async function fetchTiffImageData(
  _key: string,
  dataId: string,
  filename: string,
) {
  const response = await fetch(
    `${basePath}/v1/data/${dataId}/pull?mapping=${filename}&version=latest`,
    {
      credentials:
        process.env.NODE_ENV === "development" ? undefined : "include",
    },
  );
  const formData = await response.formData();
  const encodedBuffer = await new Promise<ArrayBuffer>((resolve, reject) => {
    const bufferReader = new FileReader();
    bufferReader.onload = () => resolve(bufferReader.result as ArrayBuffer);
    bufferReader.onerror = reject;
    bufferReader.readAsArrayBuffer(formData.get("files") as File);
  });
  const [ifd] = UTIF.decode(encodedBuffer);
  UTIF.decodeImage(encodedBuffer, ifd);
  const clampedArray = new Uint8ClampedArray(UTIF.toRGBA8(ifd));
  const { width, height } = ifd;
  const imageData = new ImageData(clampedArray, width, height);
  return {
    imageData,
    width,
    height,
  };
}

export async function getMe() {
  const { data } = await meApi.v1MeGet();
  // /v1/me should never be a Role in the webapp context
  return data as User;
}
